/**
 * Prompt builder for Radar action items.
 *
 * Produces context-rich, copy-pasteable prompts that users can paste into
 * AI tools (Claude, ChatGPT, Cursor) to implement specific fixes.
 * Pure client-side string construction -- no LLM calls.
 */

import {
  TOOLS,
  type ActionItem,
  type ToolKey,
  type ToolState,
} from '@/types/radar'
import {
  isCrawlRetrievalBot,
  type CrawlBotResult,
} from '@/lib/tools/crawl-bots'
import { hasUsableScore } from './tool-scores'
import { PROMPT_TEMPLATES } from './prompt-templates'
import { readAnswerEngineSources } from './answer-engine-result'

/* eslint-disable @typescript-eslint/no-explicit-any */

// ── Main Export ────────────────────────────────────────────────────────────────

/**
 * Placeholder used wherever a generator would otherwise assert an entity name
 * we have not verified. Publishing a guessed name into Organization schema or
 * llms.txt actively degrades the first-party identity signals the audit exists
 * to improve — the wreb.de run told a company whose homepage already declared
 * `legalName: "WREB.DEsign GmbH"` to publish `name: "WREB"` instead.
 */
export const UNVERIFIED_NAME_PLACEHOLDER =
  '<!-- your exact registered organization name -->'

const CANONICAL_TOOL_KEYS = new Set<string>(TOOLS.map(tool => tool.key))

/**
 * Persisted actions may use the public endpoint slug instead of the canonical
 * ToolKey. Derive the aliases from TOOLS so this compatibility layer cannot
 * drift from the tool registry.
 */
const LEGACY_TOOL_KEY_ALIASES = new Map<string, ToolKey>(
  TOOLS.map(tool => [
    tool.endpoint.slice(tool.endpoint.lastIndexOf('/') + 1),
    tool.key,
  ])
)

function normalizeToolKey(source: unknown): ToolKey | null {
  if (typeof source !== 'string') return null

  const candidate = LEGACY_TOOL_KEY_ALIASES.get(source) ?? source
  return CANONICAL_TOOL_KEYS.has(candidate) ? (candidate as ToolKey) : null
}

export function buildActionPrompt(
  action: ActionItem,
  tools: Record<ToolKey, ToolState>,
  domain: string,
  brand: string,
  /**
   * Whether the resolved entity is trustworthy enough to WRITE onto the
   * customer's site (high confidence, or human-confirmed — see
   * isAssertableEntity). Defaults to false so a caller that has not been
   * updated cannot silently assert an unverified name.
   */
  entityAssertable = false
): string {
  const template = PROMPT_TEMPLATES[action.category]
  if (!template)
    return buildGenericPrompt(action, domain, brand, entityAssertable)

  const context = buildContext(action, tools, domain, brand, entityAssertable)
  const steps = fillPlaceholders(template.steps, context)
  const verification = fillPlaceholders(template.verification, context)

  return [
    `You are helping implement an AI visibility fix for ${domain} (brand: ${brand}).`,
    ...(entityAssertable
      ? []
      : [
          '',
          `NOTE: the brand name above was NOT verified against ${domain}. Do not write it into schema markup, llms.txt, or any published metadata — confirm the exact registered name with the site owner first.`,
        ]),
    '',
    '## Context',
    '',
    action.why,
    '',
    '## What to Fix',
    '',
    action.title,
    '',
    '## Implementation Steps',
    '',
    steps,
    '',
    '## How to Verify',
    '',
    verification,
    '',
    '---',
    `Source: Radar AI Visibility Audit | Category: ${template.category} | Effort: ${action.effort} | Impact: ${action.impact}`,
  ].join('\n')
}

// ── Context Extraction ─────────────────────────────────────────────────────────

function buildContext(
  action: ActionItem,
  tools: Record<ToolKey, ToolState>,
  domain: string,
  brand: string,
  entityAssertable = false
): Record<string, string> {
  // `{brand}` reaches publishable output (the llms.txt title line in
  // PROMPT_TEMPLATES), so an unverified name must not be substituted into it.
  const ctx: Record<string, string> = {
    domain,
    brand: entityAssertable ? brand : UNVERIFIED_NAME_PLACEHOLDER,
  }

  // Crawl data
  const crawl = tools.crawl?.data
  if (crawl) {
    const blocked =
      crawl.botAccess?.filter(
        (b: CrawlBotResult) => !b.allowed && isCrawlRetrievalBot(b.bot)
      ) || []
    ctx.blockedBots =
      blocked.length > 0
        ? blocked.map((b: CrawlBotResult) => b.bot).join(', ')
        : 'none detected'
  }

  // Schema audit data
  const schema = tools.schemaAudit?.data
  if (schema) {
    const missing = schema.schemaTypes?.filter((t: any) => !t.found) || []
    const incomplete =
      schema.schemaTypes?.filter(
        (t: any) => t.found && t.evaluated !== false && t.completeness < 70
      ) || []
    const missingPageType = schema.hasPageTypeSchema === false

    const missingLabels = missing.map((t: any) => t.type)
    if (missingPageType) {
      missingLabels.push(
        'page type (WebPage/Article/FAQPage/Product/CreativeWork)'
      )
    }

    ctx.currentSchemaState =
      missingLabels.length > 0
        ? `Missing types: ${missingLabels.join(', ')}. ` +
          (incomplete.length > 0
            ? `Incomplete types: ${incomplete.map((t: any) => `${t.type} (${t.completeness}%)`).join(', ')}`
            : '')
        : incomplete.length > 0
          ? `All key types found but incomplete: ${incomplete.map((t: any) => `${t.type} (${t.completeness}%)`).join(', ')}`
          : 'Basic schema types detected'

    ctx.structuredDataSteps = buildStructuredDataSteps({
      currentSchemaState: ctx.currentSchemaState,
      missing,
      incomplete,
      missingPageType,
      domain,
      brand,
      entityAssertable,
    })
  }

  // llms.txt data
  const llms = tools.llms?.data
  if (llms) {
    if (!llms.llmsTxtFound) {
      ctx.llmsSpecificAdvice =
        'Your site has no llms.txt file. Create one from scratch using the structure above.'
    } else {
      const weak =
        llms.scoreBreakdown
          ?.filter((s: any) => s.score < s.maxScore * 0.5)
          .map((s: any) => s.category) || []
      ctx.llmsSpecificAdvice =
        weak.length > 0
          ? `Your llms.txt exists but these sections need improvement: ${weak.join(', ')}. Add more detail, internal links, and entity definitions.`
          : 'Your llms.txt exists and covers the basics. Focus on adding llms-full.txt for expanded context.'
    }
  }

  // Citation data
  const citation = tools.citation?.data
  if (citation) {
    const providers = citation.providers || []
    const available = providers.filter((p: any) => p.available)
    const rates = available.map(
      (p: any) =>
        `${p.provider}: ${Math.round((p.overallMentionRate || 0) * 100)}%`
    )
    ctx.citationState =
      rates.length > 0
        ? `Mention rates: ${rates.join(', ')}`
        : 'No citation data available'

    const uncited = available
      .filter((p: any) => (p.overallMentionRate || 0) < 0.1)
      .map((p: any) => p.provider)
    ctx.uncitedProviders =
      uncited.length > 0
        ? uncited.join(', ')
        : 'you are mentioned by all tested providers'
  }

  // Reddit data
  const reddit = tools.reddit?.data
  if (reddit) {
    ctx.redditState =
      reddit.totalMentions > 0
        ? `${reddit.totalMentions} mention(s) found across Reddit`
        : 'Zero Reddit mentions found'

    ctx.seedingAdvice =
      reddit.seedingMatrix?.likelySeeded > 0
        ? `WARNING: ${reddit.seedingMatrix.likelySeeded} mention(s) show signs of artificial promotion. Shift to organic engagement immediately.`
        : 'No seeding detected. Keep your engagement authentic.'
  }

  // AEO data
  const aeo = tools.aeo?.data
  if (aeo?.categories) {
    const cats = aeo.categories
    const scores = Object.entries(cats).map(
      ([key, val]: [string, any]) => `${key}: ${val.score}/${val.maxScore}`
    )
    ctx.aeoState =
      scores.length > 0
        ? `Category scores: ${scores.join(', ')}`
        : 'No AEO data available'
  }

  // Answer engine data
  const answerState = tools.answer
  const answer = answerState?.data
  if (answer) {
    ctx.answerState = hasUsableScore(answerState)
      ? `Citation test score: ${answer.overallScore}/100`
      : 'Citation test score withheld because provider coverage was insufficient'
    ctx.targetQuestion = answer.question || 'your target question'
    // Verified competitors and other cited sources are different claims and
    // are reported as such. Never label an unverified citation a competitor.
    const answerSources = readAnswerEngineSources(answer)
    ctx.competitorAdvice =
      answerSources.verifiedCompetitorUrls.length > 0
        ? `Verified competitor pages being cited instead: ${answerSources.verifiedCompetitorUrls.join(', ')}. Analyze what they include that your page does not.`
        : answerSources.otherSourceUrls.length > 0
          ? `No verified competitor URLs were supplied. Other sources cited for this question: ${answerSources.otherSourceUrls.slice(0, 5).join(', ')}. Treat these as sources, not competitors.`
          : 'No competitor or other source URLs detected in AI responses.'
  }

  // Source influence data
  const source = tools.sourceInfluence?.data
  if (source) {
    ctx.sourceState = source.brandInSources
      ? `Your domain appears in the top cited sources for your category.`
      : `Your domain is NOT among the top cited sources for your category.`

    ctx.topSources =
      source.topSources?.length > 0
        ? source.topSources
            .slice(0, 5)
            .map((s: any) => `${s.domain} (${s.mentionCount} citations)`)
            .join(', ')
        : 'no top sources identified'
  }

  // SOV data
  if (tools.promptSov?.data) {
    ctx.sovSteps = buildSovSteps(tools, action.how)
  }

  // Hallucination data
  const hallucination = tools.hallucinationCheck?.data
  if (hallucination) {
    const high = hallucination.bySeverity?.high || 0
    const medium = hallucination.bySeverity?.medium || 0
    const withheld =
      hallucination.scoreStatus === 'withheld' ||
      hallucination.overallScore == null
    ctx.hallucinationState = withheld
      ? 'Score withheld because no AI response could be evaluated against verified website evidence'
      : `${high} high-severity, ${medium} medium-severity hallucination finding(s) detected`

    const claims = Array.isArray(hallucination.flags) ? hallucination.flags : []
    ctx.falseClaims =
      claims.length > 0
        ? claims
            .slice(0, 5)
            .map(
              (c: any) =>
                `"${c.claim}" (${c.provider}, severity: ${c.severity})`
            )
            .join('\n   - ')
        : withheld
          ? 'no findings were scored because the evaluation was withheld'
          : 'no supported false claims detected'
    if (ctx.falseClaims && claims.length > 0) {
      ctx.falseClaims = '- ' + ctx.falseClaims
    }
  }

  return ctx
}

// ── Schema Instruction Helpers ─────────────────────────────────────────────────

function buildSchemaInstruction(
  type: string,
  domain: string,
  brand: string,
  entityAssertable = false
): string {
  // Never write an unverified name into a schema instruction: the customer
  // would publish it, overwriting whatever correct identity their site already
  // declares. Ask for the name instead of guessing it.
  const orgName = entityAssertable ? `"${brand}"` : UNVERIFIED_NAME_PLACEHOLDER
  switch (type) {
    case 'Organization':
      return `   - Organization schema (homepage): Add JSON-LD with @type: Organization, name: ${orgName}, url: "${domain}", plus logo, description, sameAs (social profiles), and contactPoint`
    case 'Article':
      return `   - Article schema (blog/content pages): Add JSON-LD with @type: Article, headline, author (linked to Person), datePublished, dateModified, image, publisher (linked to Organization), description`
    case 'BreadcrumbList':
      return `   - BreadcrumbList schema (all pages with navigation): Add JSON-LD with @type: BreadcrumbList and itemListElement array showing the page hierarchy`
    case 'FAQPage':
      return `   - FAQPage schema (Q&A content): Add JSON-LD with @type: FAQPage and mainEntity array of Question/Answer pairs`
    case 'Product':
      return `   - Product schema (product pages): Add JSON-LD with @type: Product, name, description, brand, offers with price and availability`
    case 'WebSite':
      return `   - WebSite schema (homepage): Add JSON-LD with @type: WebSite, name, url, description, and potentialAction for site search`
    case 'PageType':
      return `   - Page-type schema: add the schema that matches each page, Article for blog posts, WebPage for landing pages, FAQPage for Q&A, Product for product pages. Every page should declare at least one.`
    default:
      return `   - ${type} schema: Add JSON-LD with @type: ${type} and all required fields per Schema.org specification`
  }
}

// ── Structured Data Step Composition ───────────────────────────────────────────

/**
 * Schema.org types Google documents as eligible for a distinct rich result
 * (Article, Breadcrumb, FAQ, Product, Organization/Logo, WebSite sitelinks
 * searchbox -- see developers.google.com/search/docs/appearance/structured-data).
 * Generic containers like CreativeWork or WebPage have no dedicated rich
 * result, so Rich Results Test has nothing to validate them against; the
 * Schema.org Validator checks the markup shape instead.
 */
const GOOGLE_RICH_RESULT_SCHEMA_TYPES = new Set([
  'Organization',
  'Article',
  'BreadcrumbList',
  'FAQPage',
  'Product',
  'WebSite',
])

function schemaValidatorAdvice(types: string[]): string {
  const unique = [...new Set(types)].filter(Boolean)
  const richResultTypes = unique.filter(t =>
    GOOGLE_RICH_RESULT_SCHEMA_TYPES.has(t)
  )
  const genericTypes = unique.filter(
    t => !GOOGLE_RICH_RESULT_SCHEMA_TYPES.has(t)
  )

  if (genericTypes.length === 0) {
    return "Validate each JSON-LD block using Google's Rich Results Test (search.google.com/test/rich-results)"
  }

  const genericList = genericTypes.join(', ')
  const genericPronoun = genericTypes.length === 1 ? 'it' : 'them'

  if (richResultTypes.length === 0) {
    return `Google does not document ${genericList} as an eligible rich-result type -- validate ${genericPronoun} using the Schema.org Validator (validator.schema.org) instead`
  }

  return `Validate ${richResultTypes.join(', ')} using Google's Rich Results Test (search.google.com/test/rich-results). Google does not document ${genericList} as an eligible rich-result type -- validate ${genericPronoun} using the Schema.org Validator (validator.schema.org) instead`
}

function buildStructuredDataSteps(input: {
  currentSchemaState: string
  missing: any[]
  incomplete: any[]
  missingPageType: boolean
  domain: string
  brand: string
  entityAssertable: boolean
}): string {
  const {
    currentSchemaState,
    missing,
    incomplete,
    missingPageType,
    domain,
    brand,
    entityAssertable,
  } = input

  const lines: string[] = [
    `1. Audit your current JSON-LD: ${currentSchemaState}`,
  ]
  let step = 2

  const addInstructions = missing.map((t: any) =>
    buildSchemaInstruction(t.type, domain, brand, entityAssertable)
  )
  if (missingPageType) {
    addInstructions.push(
      buildSchemaInstruction('PageType', domain, brand, entityAssertable)
    )
  }
  if (addInstructions.length > 0) {
    lines.push(
      `${step}. Add the missing schema types to your pages:\n${addInstructions.join('\n')}`
    )
    step += 1
  }

  if (incomplete.length > 0) {
    const improveInstructions = incomplete.map((t: any) => {
      const fields =
        t.missingFields?.length > 0
          ? t.missingFields.join(', ')
          : 'the fields Schema.org requires for this type'
      return `   - ${t.type} schema is incomplete (${t.completeness}%): add ${fields}`
    })
    lines.push(
      `${step}. Complete these existing schema types:\n${improveInstructions.join('\n')}`
    )
    step += 1
  }

  // Cross-signal identity guidance is only relevant once the audit has
  // actually flagged the Organization identity node itself -- otherwise
  // there is no evidence of a divergence to fix, and the instruction would
  // be generic filler rather than a finding.
  const organizationIssue =
    missing.some((t: any) => t.type === 'Organization') ||
    incomplete.some((t: any) => t.type === 'Organization')
  if (organizationIssue) {
    lines.push(
      `${step}. Ensure consistent factual identity across signals: your JSON-LD organization name, meta title, and llms.txt should agree on name, category, and domain, with descriptions that do not conflict.`
    )
    step += 1
  }

  const validatedTypes = [
    ...missing.map((t: any) => t.type),
    ...incomplete.map((t: any) => t.type),
  ].filter(t => t !== 'PageType')
  lines.push(`${step}. ${schemaValidatorAdvice(validatedTypes)}`)
  step += 1

  lines.push(
    `${step}. Deploy and verify with a view-source check (Ctrl+U) to confirm JSON-LD is in the HTML`
  )

  return lines.join('\n')
}

// ── SOV Step Composition ───────────────────────────────────────────────────────

/**
 * Bots Crawl Check currently finds blocked, restricted to entries that are
 * actually an SOV-provider crawl defect: an AI browsing bot (not a training
 * or search/SEO-only bot -- those cover different concerns), explicitly
 * blocked (not merely unverifiable), with a real name. Read fresh from
 * `tools` (never cached on ctx) so the single-action SOV prompt and a thread
 * step's shared block can't disagree about crawl state.
 */
function blockedCrawlBots(tools: Record<ToolKey, ToolState>): string[] {
  const botAccess = tools.crawl?.data?.botAccess || []
  const blocked = botAccess.filter(
    (b: CrawlBotResult) =>
      b.allowed === false &&
      isCrawlRetrievalBot(b.bot) &&
      b.verifiable !== false &&
      typeof b.bot === 'string' &&
      b.bot.trim().length > 0
  )
  return [
    ...new Set<string>(
      blocked.map((b: CrawlBotResult): string => b.bot.trim())
    ),
  ]
}

/**
 * A missing SOV provider is a content/authority gap, not necessarily a crawl
 * problem -- a bot with full access can still choose not to mention a brand.
 * Only name a crawl fix when Crawl Check has actually found a blocked AI
 * browsing bot; otherwise this would be unconditional advice unsupported by
 * the evidence (see the "Expand SOV" action's `how` in actions.ts for the
 * same fix applied to that action's own copy). Wording stays cause-neutral:
 * Crawl Check found an access issue, but that has not been shown to be the
 * cause of the SOV gap until the same prompt set is retested.
 */
function buildSovCrawlNote(tools: Record<ToolKey, ToolState>): string {
  const bots = blockedCrawlBots(tools)
  if (bots.length === 0) return ''
  return `Crawl Check also found blocked AI bot(s) with browsing access (${bots.join(', ')}) -- this is a separate access issue, not confirmed as the cause of the SOV gap. Fix robots.txt to allow them, then retest the same prompt set to see whether SOV changes.`
}

function buildSovFields(sov: any): {
  sovState: string
  sovCompetitors: string
  missingProviders: string
} {
  const hasTarget = (sov.targetSOV?.mentionCount || 0) > 0
  const rank = sov.targetSOV?.rank || 0
  const pct = sov.targetSOV?.sovPercent || 0
  const sovState = hasTarget
    ? `You rank #${rank} with ${pct}% share of voice`
    : 'You are not appearing in AI recommendations for your category'

  const sovCompetitors =
    sov.competitors?.length > 0
      ? sov.competitors
          .slice(0, 3)
          .map((c: any) => `${c.brand} (${c.sovPercent}%)`)
          .join(', ')
      : 'no competitors identified'

  const appearing = sov.targetSOV?.providers || []
  const attempted = sov.activeProviders || sov.usableProviders || []
  const usable = sov.usableProviders || attempted
  const notMentioned = usable.filter(
    (provider: string) => !appearing.includes(provider)
  )
  const notEvaluated = attempted.filter(
    (provider: string) => !usable.includes(provider)
  )
  const coverageNotes = [
    notMentioned.length > 0
      ? `not mentioned in evaluated responses from ${notMentioned.join(', ')}`
      : '',
    notEvaluated.length > 0
      ? `not evaluated for ${notEvaluated.join(', ')}`
      : '',
  ].filter(Boolean)
  const missingProviders =
    coverageNotes.length > 0
      ? coverageNotes.join('; ')
      : 'you appear in all providers with usable responses'

  return { sovState, sovCompetitors, missingProviders }
}

/**
 * Shared SOV guidance -- state, competitor gap, content-authority steps, and
 * provider expansion -- rendered ONCE per SOV thread step no matter how many
 * SOV actions the step bundles. Previously each action re-ran the full `sov`
 * template, so a step with two SOV actions (e.g. "Outrank ..." and "Expand
 * SOV to N more...") printed this identical block twice. Deliberately
 * excludes Product/Service schema, sameAs links, and llms.txt -- those are
 * owned by the structured-data and llm-communication threads, and asserting
 * them here duplicated (and could contradict) that advice.
 *
 * `actionHow` is optional, action-specific implementation guidance appended
 * as a final step. The single-action prompt (buildActionPrompt, via
 * buildContext) passes `action.how` here because the SOV template has no
 * other place to surface it. A thread step's shared block must NOT pass it
 * -- ThreadsPanel renders each action's own `how` in its own section right
 * after this shared block, so passing it here would duplicate it.
 */
export function buildSovSteps(
  tools: Record<ToolKey, ToolState>,
  actionHow?: string
): string {
  const sov = tools.promptSov?.data
  if (!sov) return ''

  const withheld = !hasUsableScore(tools.promptSov)
  const { sovState, sovCompetitors, missingProviders } = withheld
    ? {
        sovState: 'Prompt SOV was not evaluated because coverage was unusable',
        sovCompetitors: 'not evaluated',
        missingProviders: 'not evaluated',
      }
    : buildSovFields(sov)

  const lines = [
    `1. Current SOV state: ${sovState}`,
    `2. Target the query types where AI recommends competitors: ${sovCompetitors}`,
    `3. Build content that directly answers "best [category]" queries:\n   - Create or strengthen a definitive comparison page/resource for your category\n   - Include your brand naturally among relevant competitors`,
    `4. Expand to AI providers where you do not appear: ${missingProviders}`,
  ]
  let step = 5

  const crawlNote = buildSovCrawlNote(tools)
  if (crawlNote) {
    lines.push(`${step}. ${crawlNote}`)
    step += 1
  }

  if (actionHow) {
    lines.push(`${step}. ${actionHow}`)
    step += 1
  }

  return lines.join('\n')
}

/**
 * Verification keys for ONE thread step, derived from the sources of the
 * actions it actually bundles -- but ONLY for steps that include at least
 * one SOV action. A step with only SOV actions must verify against Prompt
 * SOV alone; printing the Citation Building Thread's five global keys
 * (citation, reddit, sourceInfluence, promptSov, hallucinationCheck) for an
 * SOV-only step told users to re-run four tools unrelated to the step they
 * just completed. A step that mixes SOV with other categories derives one
 * key per distinct action source instead (still narrower than the thread's
 * static list). `crawl` is added only when Crawl Check has an actual blocked
 * AI-browse bot -- matching buildSovCrawlNote's condition for prescribing
 * that fix in the first place.
 *
 * Non-SOV steps retain the thread's existing static `fallbackKeys`
 * unconditionally -- their tool-to-action mapping was already correct and
 * this derivation only exists to narrow the SOV case. `fallbackKeys` is also
 * the answer when an SOV step's own derivation comes up empty (e.g. every
 * action maps to a NonToolActionSource), so callers must pass the thread's
 * `verifyToolKeys` here rather than deriving it twice.
 */
export function deriveStepVerifyKeys(
  actionIds: string[],
  actions: ActionItem[],
  tools: Record<ToolKey, ToolState>,
  fallbackKeys: ToolKey[]
): ToolKey[] {
  const stepActions = actions.filter(a => actionIds.includes(a.id))
  if (!stepActions.some(a => a.category === 'sov')) {
    return fallbackKeys
  }

  const keys: ToolKey[] = []
  for (const action of stepActions) {
    const key = normalizeToolKey(action.source)
    if (key && !keys.includes(key)) keys.push(key)
  }
  if (blockedCrawlBots(tools).length > 0 && !keys.includes('crawl')) {
    keys.push('crawl')
  }
  return keys.length > 0 ? keys : fallbackKeys
}

// ── Utility ────────────────────────────────────────────────────────────────────

function fillPlaceholders(
  template: string,
  context: Record<string, string>
): string {
  const filled = template.replace(/\{(\w+)\}/g, (match, key) =>
    key in context ? context[key] : match
  )
  // Invariant: a rendered prompt must never carry a literal, unresolved
  // placeholder. An optional value that resolved to '' is dropped cleanly
  // above; anything still bracketed here is a key no category populated,
  // and must never leak to customer output.
  return filled.replace(/\{\w+\}/g, '')
}

function buildGenericPrompt(
  action: ActionItem,
  domain: string,
  brand: string,
  entityAssertable = false
): string {
  return [
    `You are helping implement an AI visibility fix for ${domain} (brand: ${brand}).`,
    ...(entityAssertable
      ? []
      : [
          '',
          `NOTE: the brand name above was NOT verified against ${domain}. Do not write it into schema markup, llms.txt, or any published metadata — confirm the exact registered name with the site owner first.`,
        ]),
    '',
    '## Context',
    '',
    action.why,
    '',
    '## What to Fix',
    '',
    action.title,
    '',
    '## How to Implement',
    '',
    action.how,
    '',
    '---',
    `Source: Radar AI Visibility Audit | Category: ${action.category} | Effort: ${action.effort} | Impact: ${action.impact}`,
  ].join('\n')
}

/* eslint-enable @typescript-eslint/no-explicit-any */
